/**
 * How easy food is to find where a section shops, and what to buy instead when it is not.
 */
import type {
  AvailabilityLevel,
  AvailabilityRating,
  Catalog,
  Match,
  PriceLevel,
  RegionCode,
  Substitute,
} from './types'
import {
  ancestors,
  categoryOfMatch,
  childrenOf,
  genericFor,
  isUnder,
  matchLabel,
  rolesOf,
} from './catalog'
import { energyPer100g } from './nutrition'

export const LEVEL_LABELS: Record<AvailabilityLevel, string> = {
  5: 'Everywhere',
  4: 'Common',
  3: 'Supermarket',
  2: 'Specialty aisle',
  1: 'Specialty only',
  0: 'Not available',
}

export const LEVEL_HINTS: Record<AvailabilityLevel, string> = {
  5: 'Any shop at all, including petrol stations and village shops',
  4: 'Any supermarket and most small grocers',
  3: 'Mid-size and large supermarkets only',
  2: "Big-city hypermarkets' international aisle, outdoor, health-food or ethnic grocers",
  1: 'Specialist shops in cities, or online: post it or carry it in',
  0: "Effectively absent: don't plan on it",
}

/** Substitutes must be at least this easy to find. */
export const SUBSTITUTE_MIN_LEVEL = 4

/** "ES-GA" → "ES"; "ES" → "ES". */
export function countryOf(region: RegionCode): RegionCode {
  const cut = region.indexOf('-')
  return cut < 0 ? region : region.slice(0, cut)
}

/**
 * A region's name for messages ("Spain"), falling back from subdivision to country, then to
 * the code itself when the catalog does not know it.
 */
export function regionName(cat: Catalog, region: RegionCode): string {
  return cat.regions.get(region)?.name ?? cat.regions.get(countryOf(region))?.name ?? region
}

/**
 * The rating in one cell for a region, falling back from subdivision to country. When both
 * exist the higher source wins (user > community > curated); on equal source, the subdivision.
 */
function ratingIn(
  cell: Record<RegionCode, AvailabilityRating> | undefined,
  region: RegionCode,
): AvailabilityRating | undefined {
  if (!cell) return undefined
  const local = cell[region]
  const country = region !== countryOf(region) ? cell[countryOf(region)] : undefined
  if (local && country)
    return SOURCE_RANK[country.source] > SOURCE_RANK[local.source] ? country : local
  return local ?? country
}

const SOURCE_RANK: Record<AvailabilityRating['source'], number> = {
  curated: 0,
  community: 1,
  user: 2,
}

/** Rating for a category, walking up to the nearest rated ancestor. */
function categoryAvailability(cat: Catalog, categoryId: string, region: RegionCode) {
  for (const id of [categoryId, ...ancestors(cat, categoryId)]) {
    const r = ratingIn(cat.availability[id], region)
    if (r) return r
  }
  return undefined
}

/** A food's own override for the region, else its category's; undefined when unrated. */
export function availabilityOf(
  cat: Catalog,
  match: Match,
  region: RegionCode | undefined,
): AvailabilityRating | undefined {
  if (!region) return undefined
  if ('food' in match) {
    const food = cat.foods.get(match.food)
    if (!food) return undefined
    const own = ratingIn(food.availability, region)
    if (own) return own
    return categoryAvailability(cat, food.category, region)
  }
  return categoryAvailability(cat, match.category, region)
}

const PRICE_ORDER: Record<PriceLevel, number> = {
  cheap: 0,
  typical: 1,
  pricey: 2,
  'very-pricey': 3,
}

function priceRank(r: AvailabilityRating): number {
  return r.price ? PRICE_ORDER[r.price] : PRICE_ORDER.typical
}

/** A choice is as easy as its easiest option: the highest level, then the cheapest. */
export function lineAvailability(
  cat: Catalog,
  matches: readonly Match[],
  region: RegionCode | undefined,
): AvailabilityRating | undefined {
  let best: AvailabilityRating | undefined
  for (const m of matches) {
    const r = availabilityOf(cat, m, region)
    if (!r) continue
    if (
      !best ||
      r.level > best.level ||
      (r.level === best.level && priceRank(r) < priceRank(best))
    ) {
      best = r
    }
  }
  return best
}

export interface SubstitutableLine {
  key: string
  accepts: Match[]
  assumed_food?: string
}

/** Energy density of the food a match would be estimated from, kcal per 100 g. */
function densityOf(cat: Catalog, match: Match): number | undefined {
  const food = 'food' in match ? cat.foods.get(match.food) : genericFor(cat, match.category)
  return food ? energyPer100g(food) : undefined
}

/**
 * Easier things to buy instead of a line, from the same part of the tree: siblings first, then
 * cousins, walking further up only when a nearer level offered nothing. A candidate must share
 * a role with the line (when the line has roles) and be rated ≥ 4 in the region. Within a
 * level, the shallowest qualifying category wins, so "pasta" is offered rather than each shape.
 */
export function substitutesFor(
  cat: Catalog,
  line: SubstitutableLine,
  region: RegionCode | undefined,
  limit = 3,
): Substitute[] {
  if (!region) return []
  const origins = line.accepts
    .map((m) => categoryOfMatch(cat, m))
    .filter((c): c is string => c !== undefined && cat.categories.has(c))
  if (!origins.length) return []

  const roles = new Set(origins.flatMap((c) => rolesOf(cat, c)))
  const lineDensity = line.assumed_food
    ? densityOf(cat, { food: line.assumed_food })
    : densityOf(cat, line.accepts[0] as Match)
  const covered = (id: string) => origins.some((o) => isUnder(cat, o, id) || isUnder(cat, id, o))

  const out: Substitute[] = []
  const seen = new Set<string>()
  for (const origin of origins) {
    // Each level: the ancestor, and the child of it on the path back to `origin`.
    const path = [origin, ...ancestors(cat, origin)]
    // Never wander across top-level groups: noodles may become pasta, not chocolate.
    for (let level = 1; level < path.length; level++) {
      const towardOrigin = path[level - 1] as string
      const anchor = path[level] as string
      const pool = childrenOf(cat, anchor)
      const found: Substitute[] = []
      const queue = pool.filter((id) => id !== towardOrigin)
      while (queue.length) {
        const id = queue.shift() as string
        if (seen.has(id) || covered(id)) continue
        const rating = availabilityOf(cat, { category: id }, region)
        const candidateRoles = rolesOf(cat, id)
        const sharesRole = roles.size === 0 || candidateRoles.some((r) => roles.has(r))
        if (rating && rating.level >= SUBSTITUTE_MIN_LEVEL && sharesRole) {
          seen.add(id)
          const match: Match = { category: id }
          const anchorName = matchLabel(cat, { category: anchor })
          const reasons = [level === 1 ? `same group: ${anchorName}` : `same family: ${anchorName}`]
          const d = densityOf(cat, match)
          if (d !== undefined && lineDensity !== undefined && lineDensity > 0) {
            if (Math.abs(d - lineDensity) / lineDensity <= 0.15) reasons.push('similar energy')
          }
          found.push({
            key: line.key,
            match,
            label: matchLabel(cat, match),
            availability: rating,
            reason: reasons.join(', '),
          })
        } else {
          queue.push(...childrenOf(cat, id))
        }
      }
      if (found.length) {
        out.push(...found)
        break
      }
    }
  }
  out.sort(
    (a, b) =>
      b.availability.level - a.availability.level ||
      priceRank(a.availability) - priceRank(b.availability) ||
      a.label.localeCompare(b.label),
  )
  return out.slice(0, limit)
}
